import { Organization } from '@enview/interface/src/types/Organization';
import { Jurisdiction } from '@enview/interface/types/Jurisdiction';
import PolicyDataType from '@enview/interface/types/PolicyDataType';
import { isEqual } from 'lodash-es';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ChevronIcon from '../../../components/svg/ChevronDownIcon';
import LocationIcon from '../../../components/svg/LocationIcon';
import { getOrgForTeamMode, getSupportedJurisdictions } from '../../../dux';
import { State } from '../../../dux/@types';
import { getLegislaturesOptions } from '../../../helpers/JurisdictionSelectionHelper';
import CustomDropdown from '../DropDowns/CustomDropdown';
import MultiSelectForm, { MultiSelectOption } from '../Forms/MultiSelectForm';
import './Selector.scss';

type JurisdictionDropdownProps = {
  jurisdictions?: Jurisdiction[];
  jurisdictionsSelected: string[];
  setJurisdictionsSelected: (arg: string[]) => void;
  setSearchChanged: Dispatch<SetStateAction<boolean>>;
  isDisabled?: boolean;
  filterByPolicyDataType?: PolicyDataType;
};

const JurisdictionDropdown = (props: JurisdictionDropdownProps): ReactElement => {
  const {
    jurisdictions,
    jurisdictionsSelected,
    setJurisdictionsSelected,
    setSearchChanged,
    isDisabled,
    filterByPolicyDataType,
  } = props;

  const organization = useSelector(getOrgForTeamMode);

  const orgUser = useSelector((state: State) => state.account.organizationUser);

  const supportedJurisdictions = useSelector(
    (state: State) => state.account.supportedJurisdictions,
  )?.filter((jurisdiction) => {
    // user is logged in
    if (!!filterByPolicyDataType) {
      // user is in a non-personal org
      if (organization) {
        return organization.jurisdictions.some(
          (orgJur) => orgJur.name == jurisdiction.name && orgJur.hasRegulationAccess,
        );
      }
      // user is in personal org
      else {
        return orgUser?.organizations.map((org: Organization) =>
          org.jurisdictions.map(
            (orgJur) => orgJur.name == jurisdiction.name && orgJur.hasRegulationAccess,
          ),
        );
      }
    }
    // user is logged out
    return true;
  });

  const legislaturesOptions =
    (supportedJurisdictions &&
      getLegislaturesOptions(
        jurisdictions || supportedJurisdictions,
        supportedJurisdictions,
      )) ||
    [];

  const [text, setText] = useState('');
  const [jurisdictionOptions, setJurisdictionOptions] = useState(legislaturesOptions);
  const [showJurisdictionFilter, setShowJurisdictionFilter] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!supportedJurisdictions) {
      dispatch(getSupportedJurisdictions());
    }
  });

  const getJurisdictionOptions = useMemo((): MultiSelectOption<string>[] => {
    const selectedOptions: MultiSelectOption<string>[] = [];
    const unselectedOptions: MultiSelectOption<string>[] = [];
    const selectedSet = new Set(jurisdictionsSelected);

    const jurOptions =
      jurisdictionOptions.length === 0 && text.length === 0
        ? legislaturesOptions
        : jurisdictionOptions;

    // Move "US Congress" to the top of dropdwon options
    const index = jurOptions.findIndex((jurOption) => jurOption.value === 'usa');
    if (index !== -1) jurOptions.unshift(...jurOptions.splice(index, 1));

    jurOptions.forEach((option) => {
      // add selected options first in alphabetical order
      if (selectedSet.has(option.value)) {
        selectedOptions.push(option);
      } else {
        unselectedOptions.push(option);
      }
    });
    return selectedOptions.concat(unselectedOptions);
  }, [jurisdictionOptions, text, legislaturesOptions, jurisdictionsSelected]);

  const handleTextChange = (inputText: string): void => {
    let suggestions: MultiSelectOption<string>[] = [];
    if (inputText.length > 0) {
      // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
      const regex = new RegExp(`^${inputText}`, `i`);
      suggestions = legislaturesOptions.sort().filter((v) => regex.test(v.label));
      setShowJurisdictionFilter(true);
    }
    if (inputText.length === 0) suggestions = legislaturesOptions;
    setJurisdictionOptions(suggestions);
    setText(inputText);
  };

  const handleJurisdictionFiltersChange = (
    updatedJurisdictionFilters: string[],
  ): void => {
    if (isEqual(updatedJurisdictionFilters, jurisdictionsSelected)) return;
    setJurisdictionsSelected(updatedJurisdictionFilters);
    handleTextChange('');
    setSearchChanged(true);
  };

  const getDropdownPlaceholder = (): string =>
    filterByPolicyDataType === PolicyDataType.Regulation
      ? 'Jurisdiction'
      : 'Legislature';

  const targetButton = (selectedCount: number): ReactElement => {
    return (
      <button
        className="d-flex btn btn-text ml-auto align-items-center"
        disabled={isDisabled}
        id="legislature-dropdown"
        onClick={() => setShowJurisdictionFilter(!showJurisdictionFilter)}
        type="button"
      >
        {selectedCount !== undefined && selectedCount > 0 && (
          <div className="dropdown-count">{selectedCount}</div>
        )}
        <ChevronIcon />
      </button>
    );
  };

  const ref = useRef<HTMLDivElement>(null);
  const elementWidth = ref.current ? ref.current.offsetWidth : undefined;

  return (
    <div className="jurisdiction-dropdown" ref={ref}>
      <div className="row flex-nowrap">
        <div className="location-icon">
          <LocationIcon />
        </div>

        <input
          disabled={isDisabled}
          onChange={(event) => handleTextChange(event.target.value)}
          placeholder={getDropdownPlaceholder()}
          type="text"
          value={text}
        />

        <CustomDropdown
          alignRight
          setShowDropdown={setShowJurisdictionFilter}
          showDropdown={showJurisdictionFilter}
          target={targetButton(jurisdictionsSelected.length)}
          width={elementWidth}
        >
          <div className="jurisdiction-dropdown-menu">
            <MultiSelectForm
              currentSelection={jurisdictionsSelected}
              options={getJurisdictionOptions}
              showClearAll
              showSelectAll
              showSelectButtonsBottom
              singleColumn
              updateSelection={handleJurisdictionFiltersChange}
            />
          </div>{' '}
        </CustomDropdown>
      </div>
    </div>
  );
};

export default JurisdictionDropdown;
